import type { ClerkAPIError, ClerkAPIResponseError } from "@clerk/types";

const KNOWN_EXISTING_IDENTIFIER_CODES = new Set([
  "form_identifier_exists",
  "identifier_exists",
  "identifier_already_exists",
]);

export function isClerkAPIResponseError(
  error: unknown,
): error is ClerkAPIResponseError {
  if (!error || typeof error !== "object") {
    return false;
  }

  if (!("errors" in error)) {
    return false;
  }

  const { errors } = error as { errors?: ClerkAPIError[] | null };
  return Array.isArray(errors);
}

export function getClerkErrorMessage(
  error: unknown,
  fallbackMessage: string,
): string {
  if (!isClerkAPIResponseError(error)) {
    if (error instanceof Error && error.message.trim().length > 0) {
      return error.message;
    }
    return fallbackMessage;
  }

  for (const clerkError of error.errors ?? []) {
    if (!clerkError) continue;
    const message =
      clerkError.longMessage ??
      clerkError.message ??
      clerkError.code ??
      fallbackMessage;
    if (message && message.trim().length > 0) {
      return message;
    }
  }

  return fallbackMessage;
}

export function isExistingIdentifierClerkError(error: unknown): boolean {
  if (!isClerkAPIResponseError(error)) {
    return false;
  }

  return (error.errors ?? []).some((clerkError) => {
    if (!clerkError) return false;

    const code = clerkError.code ?? undefined;
    if (code && KNOWN_EXISTING_IDENTIFIER_CODES.has(code)) {
      return true;
    }

    const normalizedMessage = (
      clerkError.longMessage ??
      clerkError.message ??
      ""
    )
      .toLowerCase()
      .trim();

    if (normalizedMessage.length === 0) {
      return false;
    }

    return (
      normalizedMessage.includes("already") &&
      (normalizedMessage.includes("exist") ||
        normalizedMessage.includes("registered") ||
        normalizedMessage.includes("in use"))
    );
  });
}
